import type { AgentId, Meeting, TaskId } from "@agentsaas/protocol";

/** 会议页面的纯逻辑（可单测） */

export type MeetingAction = "start" | "next-round" | "close";
export type Minutes = NonNullable<Meeting["minutes"]>;

/** 当前可做的操作：created 可开始 / 直接结束；running 一轮说完（没人在发言）且没到上限时可推进下一轮，随时可结束 */
export function availableActions(m: Pick<Meeting, "state" | "round" | "maxRounds" | "currentSpeaker">): Record<MeetingAction, boolean> {
  return {
    start: m.state === "created",
    "next-round": m.state === "running" && m.currentSpeaker === null && m.round < m.maxRounds,
    close: m.state === "created" || m.state === "running",
  };
}

/** 议程：每行一项，去掉 "- " / "1. " / "• " 前缀和空行 */
export function parseAgenda(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)、])\s*/u, "").trim())
    .filter(Boolean);
}

/** 轮次进度 0–1（结束 = 1） */
export function roundProgress(m: Pick<Meeting, "state" | "round" | "maxRounds">): number {
  if (m.state === "closed" || m.state === "summarizing") return 1;
  return Math.min(1, Math.max(0, m.round / m.maxRounds));
}

/** 美元字符串 → micro-USD；空串 = null；非法返回 undefined */
export function usdToMicro(v: string): number | null | undefined {
  const s = v.trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 1_000_000);
}

// ───────────── 纪要的本地编辑稿 ─────────────
// 契约没有"修改纪要"接口（已提给集成者）：编辑稿只保存在本机，行动项可以一键建任务（taskCreate）并回填 taskId。

const DRAFT_KEY = (mid: string) => `as.minutes.${mid}`;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const storage = (): Store | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
};

export function loadDraft(mid: string, s: Store | null = storage()): Minutes | null {
  try {
    const raw = s?.getItem(DRAFT_KEY(mid));
    if (!raw) return null;
    const v = JSON.parse(raw) as Minutes;
    return typeof v.summary === "string" && Array.isArray(v.decisions) && Array.isArray(v.actionItems) ? v : null;
  } catch {
    return null;
  }
}

export function saveDraft(mid: string, m: Minutes, s: Store | null = storage()) {
  try {
    s?.setItem(DRAFT_KEY(mid), JSON.stringify(m));
  } catch {
    /* 存储满 / 隐私模式 */
  }
}

export function clearDraft(mid: string, s: Store | null = storage()) {
  try {
    s?.removeItem(DRAFT_KEY(mid));
  } catch {
    /* ignore */
  }
}

/** 编辑时的行动项 */
export interface ActionItemDraft {
  title: string;
  assignee: AgentId | null;
  taskId: TaskId | null;
}

/** 清理编辑稿：去掉空的决议 / 行动项 */
export function normalizeMinutes(m: Minutes): Minutes {
  return {
    summary: m.summary.trim(),
    decisions: m.decisions.map((d) => d.trim()).filter(Boolean),
    actionItems: m.actionItems.map((a) => ({ ...a, title: a.title.trim() })).filter((a) => a.title),
  };
}

/** 纪要导出为 Markdown（复制到剪贴板用）；标题等文案由调用方传入（已翻译） */
export function minutesMarkdown(
  title: string,
  m: Minutes,
  labels: { summary: string; decisions: string; actions: string },
  nameOf: (aid: AgentId) => string,
): string {
  const lines = [`# ${title}`, "", `## ${labels.summary}`, "", m.summary, "", `## ${labels.decisions}`, ""];
  for (const d of m.decisions) lines.push(`- ${d}`);
  lines.push("", `## ${labels.actions}`, "");
  for (const a of m.actionItems) lines.push(`- [ ] ${a.title}${a.assignee ? ` (@${nameOf(a.assignee)})` : ""}`);
  return lines.join("\n");
}
